/**
 * Queues and processes worker responses to smooth out data processing
 * Prevents frame spikes when multiple workers finish simultaneously
 *
 * Now designed to be called from the main render loop with time budgeting
 * instead of running its own separate RAF loop.
 */
class WorkerResponseQueue {
  private queue: Array<{ callback: () => void; timestamp: number }> = [];
  private timeBudgetMs = 8; // Max ms to spend processing per frame (half a frame at 60fps)

  /**
   * Enqueue a worker response callback to be processed
   */
  enqueue(callback: () => void): void {
    this.queue.push({
      callback,
      timestamp: performance.now(),
    });
  }

  /**
   * Process queued callbacks within a time budget.
   * Call this from the main render loop to avoid separate RAF blocking.
   * Returns the time spent processing in ms.
   */
  processWithBudget(budgetMs: number = this.timeBudgetMs): number {
    if (this.queue.length === 0) return 0;

    const startTime = performance.now();
    let processedCount = 0;

    while (this.queue.length > 0) {
      const elapsed = performance.now() - startTime;
      if (elapsed >= budgetMs) {
        break; // Time budget exhausted
      }

      const item = this.queue.shift();
      if (!item) break;
      try {
        item.callback();
        processedCount++;
      } catch (error) {
        console.error('Error processing worker response:', error);
      }
    }

    const totalTime = performance.now() - startTime;

    // Track blocking time for profiling
    const win = window as Window & { PPCLoaderDebugWidget?: { trackBlockTime(ms: number): void } };
    if (processedCount > 0 && typeof win.PPCLoaderDebugWidget !== 'undefined') {
      win.PPCLoaderDebugWidget?.trackBlockTime(totalTime);
    }

    return totalTime;
  }

  /**
   * Process exactly one queued callback per call.
   * Use this to limit frame spikes by only doing one heavy operation per frame.
   * Returns the time spent processing in ms.
   */
  processOne(): number {
    if (this.queue.length === 0) return 0;

    const startTime = performance.now();

    const item = this.queue.shift();
    if (!item) return 0;
    try {
      item.callback();
    } catch (error) {
      console.error('Error processing worker response:', error);
    }

    const totalTime = performance.now() - startTime;

    // Track blocking time for profiling
    const win = window as Window & { PPCLoaderDebugWidget?: { trackBlockTime(ms: number): void } };
    if (typeof win.PPCLoaderDebugWidget !== 'undefined') {
      win.PPCLoaderDebugWidget?.trackBlockTime(totalTime);
    }

    return totalTime;
  }

  /**
   * Get current queue size (for debugging)
   */
  getQueueSize(): number {
    return this.queue.length;
  }

  /**
   * Get the oldest pending item's age in milliseconds
   */
  getOldestItemAge(): number {
    if (this.queue.length === 0) return 0;
    return performance.now() - this.queue[0].timestamp;
  }

  /**
   * Set the default time budget for processing
   */
  setTimeBudget(ms: number): void {
    this.timeBudgetMs = Math.max(1, ms);
  }

  /**
   * Check if there are pending items
   */
  hasPending(): boolean {
    return this.queue.length > 0;
  }

  /**
   * Clear all pending items from the queue.
   * Call this on page refresh/viewer init to prevent stale callbacks from accumulating.
   */
  clear(): void {
    this.queue = [];
  }
}

const workerResponseQueue = new WorkerResponseQueue();

export default workerResponseQueue;
